// PowerSync local-first dispatch for `createSession`.
//
// Two branches share this entry point — both end with the screen
// holding a SessionSummary it can hand off to BuildingCaptureContent
// or SiteCaptureContent:
//
//   1. Create (no existing session): generate a UUID v4, INSERT the
//      capture_sessions row locally, return the SessionSummary. The
//      connector (connector-dispatch-session-insert.ts) picks up the
//      SDK CRUD PUT and POSTs to `/capture/sessions` with the same
//      id (idempotent retry on same id; 409 already_exists when an
//      active session for (user, scope) exists at a different id —
//      a multi-device race, handled by the connector via a local
//      DELETE of the orphan).
//
//   2. Resume (the user has a completed session for this scope in
//      the local mirror): UPDATE the existing row to clear
//      completed_at and bump sync_version. The schema's
//      trackPrevious carries the non-null → null transition that
//      connector-dispatch-session.ts translates into POST
//      /capture/sessions/:id/reopen — the same path the in-UI
//      Reopen toggle uses.
//
// Why the resume branch exists: without it, tapping "Resume capture"
// on a completed building INSERTed a fresh row with a new client id.
// The server's pre-reopen conflict gate (PR #549, applied to staging
// + main) sees the requested id !== the completed session's id and
// throws 409 already_exists. The connector then deletes the local
// orphan and the screen bounces back to the "Ready to resume"
// empty-state in a loop (the completed authoritative session never
// surfaces in useActiveSession because it filters
// completed_at IS NULL). Reopening in-place keeps the id stable and
// routes the upload through the PATCH path, which has no conflict
// gate.
//
// The captures sync stream syncs the user's FULL session history (one
// bucket per user — powersync/sync-config.yaml), so a completed session is
// in the local mirror once the device has synced it. The resume-vs-create
// router (capture-client-powersync-create-session-route.ts) still diverts
// the mirror-lag case (a completed session the device has not received yet)
// to the REST transport BEFORE this dispatch runs.
//
// Site/base/building lookups + the SessionSummary assembly happen
// after the branch decision so both paths return identically shaped
// envelopes — consumers downstream (screens, useActiveSession
// reconcilers) see the same fields whether the adapter took the
// create or resume branch.
//
// Emits `session-created` either way — the event reflects the
// dispatch contract (kind: 'createSession') rather than the
// underlying SQL operation. Callers that need to disambiguate can
// inspect the returned session's sync_version (> 1 → resumed).

import type { BuildingStatus } from '@citycare/shared'

import type {
  CaptureMutation,
  CaptureMutationResult,
} from './capture-client.types'
import { eventForResult } from './capture-client-events'
import type { CaptureEventBus } from './capture-events'

import type { PowerSyncDb } from './capture-client-powersync'

/** Wrapped for testability: tests inject a fixed UUID generator so
 * assertions against the local row's id are deterministic. The
 * runtime path uses crypto.randomUUID() directly. */
export function generateSessionUuid(): string {
  return crypto.randomUUID()
}

interface SiteLookupRow {
  name: string
  baseId: string
}

interface BaseLookupRow {
  name: string
}

interface BuildingLookupRow {
  name: string
  floc: string | null
  status: BuildingStatus
}

interface ExistingSessionRow {
  id: string
  syncVersion: number
  completedAt: string | null
  startedAt: string
  createdAt: string
  sourceDeviceId: string | null
  syncedAt: string | null
}

export async function dispatchCreateSession(
  mutation: Extract<CaptureMutation, { kind: 'createSession' }>,
  db: PowerSyncDb,
  bus: CaptureEventBus,
): Promise<CaptureMutationResult> {
  const nowIso = new Date().toISOString()

  // Resume detection — look up the user's most recent session at
  // this scope. If it's completed, the create flow becomes an
  // in-place reopen instead of a fresh INSERT. An active session at
  // the same scope would have short-circuited the screen long before
  // it reached the StartCapture CTA, so in practice this query
  // returns either no row (true create) or a completed row (resume).
  const existingRows = await db.getAll<ExistingSessionRow>(
    mutation.buildingId !== null
      ? `SELECT id           AS id,
                sync_version AS syncVersion,
                completed_at AS completedAt,
                started_at   AS startedAt,
                created_at   AS createdAt,
                source_device_id AS sourceDeviceId,
                synced_at    AS syncedAt
         FROM capture_sessions
         WHERE user_id = ? AND building_id = ?
         ORDER BY started_at DESC
         LIMIT 1`
      : `SELECT id           AS id,
                sync_version AS syncVersion,
                completed_at AS completedAt,
                started_at   AS startedAt,
                created_at   AS createdAt,
                source_device_id AS sourceDeviceId,
                synced_at    AS syncedAt
         FROM capture_sessions
         WHERE user_id = ? AND site_id = ? AND building_id IS NULL
         ORDER BY started_at DESC
         LIMIT 1`,
    mutation.buildingId !== null
      ? [mutation.userId, mutation.buildingId]
      : [mutation.userId, mutation.siteId],
  )
  const existing = existingRows[0]
  const isResume = existing !== undefined && existing.completedAt !== null

  // Site is required — sync_config "reference_site_scoped" guarantees
  // every site the user has access to is in the local mirror, so this
  // is a synchronous read.
  const siteRows = await db.getAll<SiteLookupRow>(
    `SELECT name AS name, base_id AS baseId FROM sites WHERE id = ? LIMIT 1`,
    [mutation.siteId],
  )
  const site = siteRows[0]
  if (!site) {
    throw new Error(
      `dispatchCreateSession: site ${mutation.siteId} not found in local PowerSync mirror — ` +
        `the user must have read access (via user_site_assignments) before starting capture there.`,
    )
  }

  // Base — same stream as sites, always present alongside the site.
  const baseRows = await db.getAll<BaseLookupRow>(
    `SELECT name AS name FROM bases WHERE id = ? LIMIT 1`,
    [site.baseId],
  )
  const baseName = baseRows[0]?.name ?? ''

  // Building — only required for building-scoped sessions. Site-only
  // sessions (infrastructure capture) leave building=null in the
  // SessionSummary, matching the REST endpoint contract.
  let buildingSummary: {
    id: string
    name: string
    floc: string | null
    status: BuildingStatus
  } | null = null
  if (mutation.buildingId !== null) {
    const buildingRows = await db.getAll<BuildingLookupRow>(
      `SELECT name AS name, floc AS floc, status AS status
       FROM buildings WHERE id = ? LIMIT 1`,
      [mutation.buildingId],
    )
    const building = buildingRows[0]
    if (!building) {
      throw new Error(
        `dispatchCreateSession: building ${mutation.buildingId} not found in local PowerSync mirror — ` +
          `the caller must have already navigated into the building via a synced site.`,
      )
    }
    // Mirror the server's status cascade on the buildings table so the
    // UI reflects the live status before the next captures sync.
    //   - resume of a completed building → in_progress (reopen cascade);
    //   - any fresh create on a not_started building → in_progress, the
    //     same flip the server applies on session create (I-API-1,
    //     create-session.ts). Offline that server flip never reaches the
    //     device, so the Mark-complete gate (effectiveStatus ===
    //     'not_started' disables) would otherwise stay greyed out for an
    //     offline-started capture. Same pattern as dispatchSessionState-
    //     Change in capture-client-powersync.ts.
    const localStatus: BuildingStatus =
      isResume && building.status === 'complete'
        ? 'in_progress'
        : building.status === 'not_started'
          ? 'in_progress'
          : building.status
    buildingSummary = {
      id: mutation.buildingId,
      name: building.name,
      floc: building.floc,
      status: localStatus,
    }
  }

  if (isResume) {
    // Resume branch: in-place UPDATE. trackPrevious on
    // capture_sessions includes completed_at + sync_version, so the
    // CRUD entry carries the non-null → null transition that
    // dispatchSessionPatch translates into POST
    // /capture/sessions/:id/reopen. No conflict gate, no orphan.
    const nextSyncVersion = existing.syncVersion + 1
    await db.execute(
      `UPDATE capture_sessions
       SET completed_at = NULL,
           sync_version = ?,
           updated_at   = ?
       WHERE id = ?`,
      [nextSyncVersion, nowIso, existing.id],
    )
    if (mutation.buildingId !== null) {
      // Guarded local cascade — idempotent so re-renders during the
      // upload window are safe. Mirrors the server's reopen-session
      // transactional cascade (reopen-session.ts).
      //
      // Write to `ps_data__buildings` (the raw underlying SQLite
      // table) rather than the `buildings` view. The view has an
      // INSTEAD OF UPDATE trigger that queues a CRUD entry on every
      // write, and the connector's uploadData dispatcher does not
      // map the `buildings` table — it would throw on the
      // unsupported op and wedge the entire upload queue (Codex P1
      // on PR #552). The backend has already flipped the row inside
      // the /reopen transaction by the time the captures sync
      // catches up, so a queued local upload would be redundant
      // anyway. Same pattern as dispatchSessionStateChange in
      // capture-client-powersync.ts (the in-UI Reopen toggle path).
      await db.execute(
        `UPDATE ps_data__buildings
         SET data = json_set(
           data,
           '$.status', 'in_progress',
           '$.updated_at', strftime('%Y-%m-%dT%H:%M:%fZ','now')
         )
         WHERE id = ?
           AND json_extract(data, '$.status') = 'complete'`,
        [mutation.buildingId],
      )
    }
    const reopenedSession = {
      id: existing.id,
      siteId: mutation.siteId,
      buildingId: mutation.buildingId,
      userId: mutation.userId,
      userDisplayName: mutation.userDisplayName,
      startedAt: existing.startedAt,
      completedAt: null,
      sourceDeviceId: existing.sourceDeviceId,
      syncedAt: existing.syncedAt,
      syncVersion: nextSyncVersion,
      createdAt: existing.createdAt,
      updatedAt: nowIso,
      building: buildingSummary,
      site: {
        id: mutation.siteId,
        name: site.name,
        baseId: site.baseId,
        baseName,
      },
    }
    const result: CaptureMutationResult = {
      kind: 'createSession',
      session: reopenedSession,
    }
    bus.emit(eventForResult(mutation, result))
    return result
  }

  // Create branch: fresh INSERT with a brand-new id. completed_at /
  // synced_at / source_device_id stay null — the user only just
  // started. sync_version starts at 1 (matches the server's
  // `.default(1)`).
  const id = generateSessionUuid()

  await db.execute(
    `INSERT INTO capture_sessions (
       id,
       site_id,
       building_id,
       user_id,
       user_display_name,
       started_at,
       completed_at,
       source_device_id,
       synced_at,
       sync_version,
       created_at,
       updated_at
     ) VALUES (?, ?, ?, ?, ?, ?, NULL, NULL, NULL, 1, ?, ?)`,
    [
      id,
      mutation.siteId,
      mutation.buildingId,
      mutation.userId,
      mutation.userDisplayName,
      nowIso,
      nowIso,
      nowIso,
    ],
  )

  if (mutation.buildingId !== null) {
    // Mirror the server's I-API-1 cascade (create-session.ts): a fresh
    // building-scoped session flips the building not_started →
    // in_progress. Guarded + idempotent so re-renders during the upload
    // window are safe, and narrowed to 'not_started' so it never
    // clobbers an in_progress/complete building. Direct ps_data__buildings
    // write (not the view) for the same reason as the resume branch
    // above — the view's INSTEAD OF trigger queues a CRUD entry the
    // connector does not map, which would wedge the upload queue
    // (Codex P1 on PR #552); the server flips the row inside the
    // createSession transaction so a queued local upload is redundant.
    await db.execute(
      `UPDATE ps_data__buildings
       SET data = json_set(
         data,
         '$.status', 'in_progress',
         '$.updated_at', strftime('%Y-%m-%dT%H:%M:%fZ','now')
       )
       WHERE id = ?
         AND json_extract(data, '$.status') = 'not_started'`,
      [mutation.buildingId],
    )
  }

  const session = {
    id,
    siteId: mutation.siteId,
    buildingId: mutation.buildingId,
    userId: mutation.userId,
    userDisplayName: mutation.userDisplayName,
    startedAt: nowIso,
    completedAt: null,
    sourceDeviceId: null,
    syncedAt: null,
    syncVersion: 1,
    createdAt: nowIso,
    updatedAt: nowIso,
    building: buildingSummary,
    site: {
      id: mutation.siteId,
      name: site.name,
      baseId: site.baseId,
      baseName,
    },
  }

  const result: CaptureMutationResult = { kind: 'createSession', session }
  bus.emit(eventForResult(mutation, result))
  return result
}
